import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { getIn, useFormik, useFormikContext } from 'formik';
// Import Components
import { Modal, Button, ExtendedInput, Empty, BusyLoader } from '@geneui/components';
import CurrencyAutoFillSection from 'components/common/CurrencyAutoFillSection';
// Import Store
import { useAppSelector } from 'store';
import { selectCurrencyRates } from 'store/slices/currencyRates';
import { selectUserSettingsCurrencyId } from 'store/slices/userData';
// Import Hooks
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Services
import { Helpers } from 'services';
// Import Types
import type { CurrencyAmounts, PartnerCurrency } from 'types';
// Import Constants
import { l } from 'constants/common';
import { POSITIVE_AMOUNT_REGEXP } from 'constants/regexp';
// Import Enums
import { MenuItemIdEnum } from 'enums';
// Import SCSS
import styles from './currencyAmountsForm.module.scss';

const { getCurrencyLabel, convertAmountToCurrencies } = Helpers;

interface Props {
    // Formik path of the parent form the amounts are written to, e.g. `challenges[0].minAmounts`
    fieldName: string;
    // Already filtered to supported currencies by the caller: every entry is rendered and required
    supportedCurrencies: PartnerCurrency[];
    title: string;
    isVisible: boolean;
    // Label / placeholder of every currency input, defaults to 'Amount'
    placeholder?: string;
    onCancel: () => void;
    onSave?: (amounts: CurrencyAmounts) => void;
}

const CurrencyAmountsForm: React.FC<Props> = ({
    fieldName,
    supportedCurrencies,
    title,
    isVisible,
    placeholder,
    onCancel,
    onSave,
}) => {
    const { t } = useTranslation();
    const { values: parentValues, setFieldValue, setFieldTouched } = useFormikContext();

    const userCurrencyId = useAppSelector(selectUserSettingsCurrencyId) ?? '';
    const totalCurrencyRates = useAppSelector(selectCurrencyRates);
    const currencyRates = useMemo(() => totalCurrencyRates?.[userCurrencyId], [totalCurrencyRates, userCurrencyId]);

    const { convertedData: currenciesDropdownData, isLoading: isLoadingCurrencyData } = useMenuItem(
        MenuItemIdEnum.Currency,
    );

    const amountLabel = placeholder ?? t(l.Amount);

    const initialValues = useMemo(() => {
        const savedAmounts = (getIn(parentValues, fieldName) ?? {}) as CurrencyAmounts;

        return supportedCurrencies.reduce<CurrencyAmounts>((acc, { CurrencyId }) => {
            acc[CurrencyId] = savedAmounts[CurrencyId] ?? '';
            return acc;
        }, {});
        // Seeded once, when the modal mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportedCurrencies, fieldName]);

    const validationSchema = useMemo(
        () =>
            Yup.object(
                supportedCurrencies.reduce<Record<string, Yup.StringSchema>>((acc, { CurrencyId }) => {
                    acc[CurrencyId] = Yup.string()
                        .required(t(l.CannotBeEmpty, { fieldName: amountLabel }))
                        .matches(POSITIVE_AMOUNT_REGEXP, t(l.AmountIsNotValid, { fieldName: amountLabel }));
                    return acc;
                }, {}),
            ),
        [supportedCurrencies, amountLabel, t],
    );

    const formik = useFormik<CurrencyAmounts>({
        initialValues,
        validationSchema,
        onSubmit: (formValues) => {
            setFieldValue(fieldName, formValues, true);
            setFieldTouched(fieldName, true, false);

            onSave?.(formValues);
        },
    });

    const { values, errors, touched, setFieldValue: setAmount, setValues, handleBlur, submitForm } = formik;

    const amountChangeHandler = (currencyId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmount(currencyId, e.target.value.replace('-', ''));
    };

    // Fills every currency with the entered amount converted from the selected currency by its rate.
    const handleClickAutoFill = ({ amount, selectedCurrency }: { amount: string; selectedCurrency: string }) => {
        setValues(
            convertAmountToCurrencies(
                supportedCurrencies.map(({ CurrencyId }) => CurrencyId),
                { amount, fromCurrency: selectedCurrency, currencyRates: totalCurrencyRates },
            ),
            true,
        );
    };

    return (
        <Modal
            size="content-size"
            background="dark-background"
            position="center"
            title={title}
            className={styles.currencyAmountsModal}
            closable={false}
            visible={isVisible}
            onCancel={onCancel}
            footer={
                <>
                    <Button color="default" appearance="minimal" onClick={onCancel}>
                        {t(l.Cancel)}
                    </Button>
                    <Button color="primary" onClick={submitForm} disabled={supportedCurrencies.length === 0}>
                        {t(l.Apply)}
                    </Button>
                </>
            }
        >
            {supportedCurrencies.length === 0 ? (
                <Empty title={t(l.NoDataFound)} />
            ) : (
                <div className={styles.currencyAmountsForm}>
                    <CurrencyAutoFillSection
                        dropdownData={currenciesDropdownData}
                        onOk={handleClickAutoFill}
                        isLoading={isLoadingCurrencyData}
                    />
                    <BusyLoader isBusy={isLoadingCurrencyData}>
                        <div className={styles.currencyAmountsList}>
                            {supportedCurrencies.map(({ CurrencyId }) => {
                                const label = getCurrencyLabel(
                                    amountLabel,
                                    values[CurrencyId],
                                    CurrencyId,
                                    userCurrencyId,
                                    currencyRates,
                                );

                                return (
                                    <div key={CurrencyId} className={styles.currencyAmountItem}>
                                        <p className={styles.currencyCode}>{CurrencyId}</p>
                                        <ExtendedInput
                                            className={styles.currencyAmountInput}
                                            name={CurrencyId}
                                            label={label}
                                            title={label}
                                            placeholder={amountLabel}
                                            labelAppearance="swap"
                                            appearance="outline"
                                            type="number"
                                            min={0}
                                            autocomplete="off"
                                            value={values[CurrencyId]}
                                            onChange={amountChangeHandler(CurrencyId)}
                                            onBlur={handleBlur}
                                            isValid={!(touched[CurrencyId] && errors[CurrencyId])}
                                            errorText={touched[CurrencyId] ? errors[CurrencyId] : undefined}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </BusyLoader>
                </div>
            )}
        </Modal>
    );
};

export default memo(CurrencyAmountsForm);
